import { createSlice } from "@reduxjs/toolkit";
import sanitizeChatItemsForUser, {
  getChatItemIdentity,
  sanitizeChatItemForUser,
} from "src/utils/sanitizeChatItemsForUser";
const indexedDB =
  window.indexedDB ||
  window.mozIndexedDB ||
  window.webkitIndexedDB ||
  window.msIndexedDB;
const openDB = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open("ChatCacheDB", 1);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("messages")) {
        db.createObjectStore("messages", { keyPath: "roomId" });
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => reject(e.target.error);
  });
const cacheMessages = async (roomId, messages) => {
  // Skip tiny cache payloads to avoid churn, without opening a write transaction.
  if (!messages) {
    return false;
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("messages", "readwrite");
    const store = tx.objectStore("messages");

    const req = store.put({ roomId, messages });

    req.onsuccess = () => resolve(true);
    req.onerror = (e) => reject(e.target.error);

    tx.oncomplete = () => db.close();
    tx.onerror = (e) => reject(e.target.error);
  });
};
const getCachedMessages = async (roomId) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction("messages", "readonly");
    const store = tx.objectStore("messages");
    const req = store.get(roomId);
    req.onsuccess = () => resolve(req.result ? req.result.messages : null);
    req.onerror = (e) => reject(e.target.error);
    tx.oncomplete = () => db.close();
    tx.onerror = (e) => reject(e.target.error);
  });
};

const getLoggedInUserIdSafe = () => {
  try {
    return JSON.parse(localStorage.getItem("user"))?._id || null;
  } catch (e) {
    return null;
  }
};

const sanitizeChatStateForCurrentUser = (items) =>
  sanitizeChatItemsForUser(items, getLoggedInUserIdSafe());

const upsertReceiptEntry = (entries, userId, time) => {
  const safeEntries = Array.isArray(entries) ? entries : [];
  if (!userId) return safeEntries;

  const alreadyPresent = safeEntries.some((e) => e?.userId === userId);
  if (alreadyPresent) return safeEntries;

  return [
    ...safeEntries,
    {
      userId,
      userAgent: null,
      _id: userId,
      time,
    },
  ];
};
const setAllRead = async ({ roomId }) => {
  const messages = await getCachedMessages(roomId);

  if (!messages) {
    console.warn(`No messages found for roomId: ${roomId}`);
    return;
  }

  if (!Array.isArray(messages)) {
    console.warn(`Invalid message format for roomId: ${roomId}`);
    return;
  }

  const loggedInUserId = getLoggedInUserIdSafe();
  const now = Date.now();

  for (const message of messages) {
    message.delivered = now;
    message.read = now;

    if (!Array.isArray(message.readBy)) {
      message.readBy = [];
    }

    const senderId = message?.senderId || message?.senderDetails?._id;
    if (loggedInUserId && senderId && senderId === loggedInUserId) {
      continue;
    }

    message.readBy = upsertReceiptEntry(message.readBy, loggedInUserId, now);
  }

  try {
    await cacheMessages(roomId, messages);
  } catch (e) {
    console.error(`Failed to update messages for roomId: ${roomId}`, e);
  }
};

const chatListSlice = createSlice({
  name: "chatList",
  initialState: { chats: [] },
  reducers: {
    addChat: (state, action) => {
      const currentUserId = getLoggedInUserIdSafe();
      const sanitizedChat = sanitizeChatItemForUser(action.payload, currentUserId);

      if (!sanitizedChat) {
        return;
      }

      state.chats = sanitizeChatStateForCurrentUser([
        ...state.chats,
        sanitizedChat,
      ]);
    },
    updateChat: (state, action) => {
      const { updatedData, status } = action.payload;
      const currentUserId = getLoggedInUserIdSafe();
      const sanitizedChat = sanitizeChatItemForUser(updatedData, currentUserId);
      const chatIdentity = getChatItemIdentity(updatedData);

      if (!chatIdentity) {
        console.error("No stable chat identity provided in updatedData");
        return;
      }

      const index = state?.chats.findIndex(
        (chat) => getChatItemIdentity(chat) === chatIdentity
      );

      if (!sanitizedChat) {
        if (index !== -1) {
          state.chats = state.chats.filter(
            (chat) => getChatItemIdentity(chat) !== chatIdentity
          );
        }
        return;
      }

      const unreadStorageKey = sanitizedChat?.roomId ?? sanitizedChat?._id;

      if (index !== -1) {
        const existingChat = state.chats[index];
        const nextGroupDetails =
          Array.isArray(sanitizedChat?.groupDetails) &&
          sanitizedChat.groupDetails.length > 0
            ? sanitizedChat.groupDetails
            : existingChat?.groupDetails;
        const unreadCount =
          localStorage.getItem(`unreadCount_${unreadStorageKey}`) || 0;
        if (status !== "matched") {
          localStorage.setItem(
            `unreadCount_${unreadStorageKey}`,
            parseInt(unreadCount) + 1,
          );
        }
        state.chats[index] = {
          ...existingChat,
          ...sanitizedChat,
          groupDetails: nextGroupDetails,
          unRead:
            status === "sent" || status === "matched"
              ? 0
              : (existingChat.unRead || parseInt(unreadCount)) + 1,
        };
        const updatedChat = state.chats.splice(index, 1)[0];
        state.chats.unshift(updatedChat);
      } else {
        state.chats.unshift({
          ...sanitizedChat,
          unRead: sanitizedChat?.unRead,
        });
      }

      state.chats = sanitizeChatStateForCurrentUser(state.chats);
    },
    resetUnReadCount: (state, action) => {
      const { roomId } = action.payload;
      if (!roomId) {
        console.error("No roomId provided for resetting unread count");
        return;
      }
      const index = state.chats.findIndex((chat) => chat.roomId === roomId);
      setAllRead({ roomId });
      localStorage.removeItem(`unreadCount_${roomId}`);
      if (index !== -1) {
        state.chats[index].unRead = 0;
      } else {
        console.warn(`Room with roomId ${roomId} not found`);
      }
    },
    removeChat: (state, action) => {
      state.chats = state.chats.filter((chat) => chat._id !== action.payload);
    },
    setChats: (state, action) => {
      state.chats = sanitizeChatStateForCurrentUser(action.payload);
    },
    setUniqueChats: (state, action) => {
      state.chats = sanitizeChatStateForCurrentUser(action.payload);
    },
    updateUnreadCount: (state, action) => {
      const { id, unreadCount } = action.payload;
      const index = state.chats.findIndex((chat) => chat._id === id);
      if (index !== -1) {
        state.chats[index].unRead = unreadCount;
      }
    },
  },
});

export const {
  addChat,
  updateChat,
  removeChat,
  setChats,
  setUniqueChats,
  updateUnreadCount,
  resetUnReadCount,
} = chatListSlice.actions;

export default chatListSlice.reducer;

// Reuse chat cache helpers across the app (e.g., Home.js)
export { openDB, cacheMessages, getCachedMessages };
